// ============================================================
// TMR Portal Admin — direct vs inherited, the rule the role editor turns on
// ============================================================

/** How one permission stands on the role being edited. */
export interface PermissionStanding {
  /** Granted on this role itself. This is the set the editor submits — nothing else is. */
  direct: boolean;
  /** Reaches the role through its parent, so it is the parent's to give or take away. */
  inherited: boolean;
  /** Whether the checkbox reads as ticked: the operator sees "the role has this", either way. */
  checked: boolean;
  /** Whether the checkbox is frozen — true only when the permission is *purely* inherited. */
  locked: boolean;
}

/**
 * Where a permission stands, given the role's direct set and what its parent grants.
 *
 * The distinction this encodes is the whole point, and getting it wrong silently destroys access: a
 * permission can be **both** direct and inherited, and the two are not interchangeable. The direct grant
 * survives the parent being changed or re-pointed; the inherited one does not. So a permission that is
 * both must stay in the submitted set — dropping it because the parent happens to cover it turns a save
 * that only fixed a typo into a revocation, and the damage does not surface until someone re-parents the
 * role much later.
 *
 * Only a purely inherited permission is locked. One that is also direct stays editable, because
 * unchecking it removes something that really exists.
 *
 * @param key The permission key from the catalogue.
 * @param direct The role's own permissions, as the editor currently holds them.
 * @param inherited The effective permissions of the selected parent role; empty when there is no parent.
 */
export function permissionStanding(
  key: string,
  direct: ReadonlySet<string>,
  inherited: ReadonlySet<string>,
): PermissionStanding {
  const isDirect = direct.has(key);
  const isInherited = inherited.has(key);

  return {
    direct: isDirect,
    inherited: isInherited,
    checked: isDirect || isInherited,
    locked: isInherited && !isDirect,
  };
}
